import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Table from "react-bootstrap/Table";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import moment from "moment";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import { Tooltip, TableCell, Pagination, Skeleton } from "@mui/material";

import "./list.css";
import { Button } from "../../common/Button";
import TextInput from "../../common/textfield/TextInput";
import SelectFiled from "../../common/textfield/SelectFiled";
import { DateSelectorWithoutFormik } from "../../common/textfield/DateSelector";
import addUserIcon from "../../../assets/images/ant-design--user-add-outlined.svg";
import EditIcon from "../../../assets/images/editIcon.svg";
import { Link } from "react-router-dom";
import EditPatientModal from "./EditPatientModal";
import { useOrgQuery } from "../../../hooks/ReactQueryHooks/useOrgQuery";
import { patientSelectionFormikObject } from "../Dashboard/Constant";
import { searchPatient } from "../../apps/api/Patient";
import { failed } from "../../common/Toastify";
import { getHCNAuthorityList } from "../../api/HCN";
import { getOrganizationSettings } from "../../api/Organization";
import { decryptData } from "../../EncryptDecrypt";
import { dateifier, ViewDate } from "../../../utils/DateSupport";

function Patients() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [hcnAuthorityList, setHcnAuthorityList] = useState([]);
  const [ecrEnabled, setEcrEnabled] = useState(false);
  const [searchData, setSearchData] = useState([]);
  const [btnLoading, setBtnLoading] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [editPatientModalShow, setEditPatientModalShow] = useState(false);
  const [selectedPatientData, setSelectedPatientData] = useState(null);

  const userRoles = useSelector(
    (state) => state?.auth?.user?.["cognito:groups"]
  );
  const permissions = useSelector(
    (state) => state?.auth?.user?.permissions
  );
  const userOrganizations = useSelector(
    (state) => state?.auth?.user?.organizations
  );
  const practitioner = userRoles?.includes("Practitioner");
  const hasAccess =
    (permissions?.includes("patient-create") && practitioner) ||
    userRoles?.includes("Super-Admin");

  const defaultOrgId = userOrganizations?.[0]?.id || "";

  useOrgQuery({
    onSuccess: (data) => {
      const options = data?.map((org) => ({
        name: org?.name,
        value: org?.id,
      }));
      setOrganizations(options);
      if (!orgId && options?.[0]?.value) {
        setOrgId(options[0].value);
        formik.setFieldValue("organization", options[0].value);
      }
    },
    refetchOnWindowFocus: false,
  });

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      birthDate: "",
      healthCardNumber: "",
      hcnAuthority: "",
      organization: defaultOrgId || "",
    },
    ...patientSelectionFormikObject,
    onSubmit: async (values) => {
      if (
        !values.firstName &&
        !values.lastName &&
        !values.birthDate &&
        !values.healthCardNumber
      ) {
        formik.setFieldError(
          "allSearchFieldsEmpty",
          "There must be a least one search criteria other than organization"
        );
        setBtnLoading(false);
        setSearchData([]);
        return;
      }
      formik.setFieldError("allSearchFieldsEmpty", undefined);
      setBtnLoading(true);
      try {
        const activeOrgId = values.organization || orgId || defaultOrgId;
        const payload = { ...values, org_id: activeOrgId };
        if (!(payload?.healthCardNumber ?? "").toString().trim()) {
          delete payload.hcnAuthority;
        }

        const res = await searchPatient(payload);
        if (res.status === true) {
          setSearchData(res.result || []);
          setPage(0);
        } else {
          setSearchData([]);
          failed(res.error || res.message || "Search failed");
        }
      } catch (err) {
        setSearchData([]);
        failed(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err.message
        );
      } finally {
        setBtnLoading(false);
      }
    },
  });

  const handleOrgChange = (event) => {
    const value = event?.target?.value;
    formik.setFieldValue("organization", value);
    setOrgId(value);
  };

  const handleDateChange = (val) => {
    if (val == null || val === "") {
      formik.setFieldValue("birthDate", "");
    }
    const momentDate = moment(val, "MMM-DD-YYYY", true);
    const isValidDate = momentDate.isValid();

    if (isValidDate) {
      formik.setFieldValue(
        "birthDate",
        moment(val).format("MM-DD-YYYY")?.replace(/-/g, "/")
      );
    } else {
      formik.setFieldValue("birthDate", val);
      formik.setFieldError("birthDate", "Invalid date format");
    }
  };

  const handleClearDate = () => {
    formik.setFieldValue("birthDate", "");
    formik.setFieldError("birthDate", undefined);
    handleDateChange(null);
  };

  useEffect(() => {
    const activeOrgId = formik.values.organization || orgId || defaultOrgId;
    if (!activeOrgId) return;

    getHCNAuthorityList(activeOrgId).then((res) => {
      if (res?.status === true) {
        const sortedAuthorities = (res.data || [])
          .slice()
          .sort((a, b) =>
            (a?.display || "").localeCompare(b?.display || "")
          );

        const allAuthorities = sortedAuthorities.map((item) => ({
          ...item,
          value: item?.authority,
          name: item?.display,
        }));

        setHcnAuthorityList(allAuthorities || []);
      }
    });

    getOrganizationSettings(activeOrgId).then((res) => {
      if (res?.status === true) {
        setEcrEnabled(res?.data?.ecrEnabled);
      }
    });
  }, [formik.values.organization, orgId, defaultOrgId]);

  const handleAuthorityChange = (e) => {
    formik.setFieldValue("hcnAuthority", e.target.value);
  };

  const handleSearchPatient = (e) => {
    e.preventDefault();
    !btnLoading && formik.handleSubmit();
  };

  const handleViewPatient = (data) => {
    const patientId = data?.id;
    if (!patientId) {
      failed(
        "Patient is sourced externally and does not exist in Florizel yet."
      );
      return;
    }
    const baseUrl = window.location.origin;
    const url = `${baseUrl}/app/patients-details?patientId=${patientId}`;
    window.open(url, "_blank");
  };

  const renderRow = (patient) => {
    const nameText = decryptData(patient?.name?.[0]?.text || "-");

    const rawBirth = patient?.birthDate
      ? decryptData(patient.birthDate)?.replace(/-/g, "/")
      : "";
    const birthParts = rawBirth ? rawBirth.split("/") : null;
    const birthFormat = birthParts ? birthParts.join("-") : "";
    const dobDisplay =
      rawBirth && dateifier(rawBirth) && ViewDate(birthFormat);

    const telecom = patient?.telecom || [];
    const email =
      telecom.find((t) => t.system === "email")?.value ||
      telecom.find((t) => t.system === "EMAIL")?.value ||
      "-";
    const phone = decryptData(
      telecom.find((t) => t.system === "phone")?.value ||
        telecom.find((t) => t.system === "PHONE")?.value ||
        "-"
    );
    const isExternalReadOnly = ecrEnabled && patient?.source === "EXT";
    const canEdit = hasAccess && !isExternalReadOnly;

    return (
      <tr key={patient?.id}>
        <td className="name-text">
          <div
            style={{
              textTransform: "capitalize",
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => handleViewPatient(patient)}
          >
            <span>{nameText}</span>
          </div>
        </td>
        <td style={{ whiteSpace: "nowrap" }}>{dobDisplay || "-"}</td>
        <td>{email}</td>
        <td style={{ whiteSpace: "nowrap" }}>{phone}</td>
        <td>
          <div className={`action-wrap ${canEdit ? "user" : ""}`}>
            {canEdit && (
              <Tooltip title="Update Details">
                <Link
                  className="view-btn"
                  onClick={() => {
                    setSelectedPatientData(patient);
                    setEditPatientModalShow(true);
                  }}
                >
                  <img src={EditIcon} alt="Update" />
                </Link>
              </Tooltip>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <section className="common-listing">
      {editPatientModalShow && (
        <EditPatientModal
          patientId={selectedPatientData?.id}
          onShow={editPatientModalShow}
          onHide={() => setEditPatientModalShow(false)}
          hasAccessToEdit={hasAccess}
          organizations={organizations}
          patientOrgId={
            selectedPatientData?.managingOrganization?.[0]?.reference
              ?.split("/")?.[1]
          }
        />
      )}
      {/* Search filters */}
      <div className="dashSearch">
        <Row>
          <Col>
            <TextInput
              keyField={"firstName"}
              label={"First Name"}
              formik={formik}
              placeholder={"First Name"}
              hideRequired={true}
            />
          </Col>
          <Col>
            <TextInput
              keyField={"lastName"}
              label={"Last Name"}
              formik={formik}
              placeholder={"Last Name"}
              hideRequired={true}
            />
          </Col>
          <Col>
            <div
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <div style={{ flex: 1 }}>
                <DateSelectorWithoutFormik
                  formik={formik}
                  keyField={"birthDate"}
                  label="DOB"
                  value={
                    formik.values.birthDate
                      ? moment(formik.values.birthDate).format("YYYY-MM-DD")
                      : null
                  }
                  handleChange={handleDateChange}
                  isError={
                    formik?.touched?.end && formik?.errors?.end
                      ? formik?.errors?.end
                      : ""
                  }
                  maxDate={moment()}
                  hideRequired={true}
                />
              </div>
              {formik.values.birthDate && (
                <Tooltip title="Clear">
                  <ClearIcon
                    style={{
                      color: "#666",
                      cursor: "pointer",
                      fontSize: "20px",
                      alignSelf: "center",
                    }}
                    onClick={handleClearDate}
                  />
                </Tooltip>
              )}
            </div>
          </Col>
          {organizations.length >= 1 && (
            <Col>
              <SelectFiled
                keyField={"organization"}
                label={"Select Organization"}
                selectLabel="Select"
                formik={formik}
                options={organizations}
                isLoading={organizations?.length >= 1 ? false : true}
                readOnly={organizations?.length >= 1 ? false : true}
                hideRequired={true}
                onChange={handleOrgChange}
              />
            </Col>
          )}
          <Col>
            <TextInput
              keyField={"healthCardNumber"}
              label={"Patient Identifier"}
              formik={formik}
              placeholder={"Patient Identifier"}
              hideRequired={true}
              tooltip={
                "Searches for exact Patient Identifier match.\n\nWarning: Using this field ignores other filters and searches by patient identifier only."
              }
            />
          </Col>
          {ecrEnabled && hcnAuthorityList?.length > 1 && (
            <Col>
              <SelectFiled
                formik={formik}
                options={hcnAuthorityList}
                fieldtype={"TextInput"}
                keyField={"hcnAuthority"}
                label={"Identifier Type"}
                placeholder={"Identifier Type"}
                hideRequired={true}
                onChange={handleAuthorityChange}
                selectDisabledValue={true}
              />
            </Col>
          )}
          <Col md="auto" style={{ marginTop: "26px" }}>
            <Button
              type="button"
              isLoading={btnLoading}
              onClick={handleSearchPatient}
            >
              <SearchOutlinedIcon /> Search
            </Button>
            {formik.errors.allSearchFieldsEmpty && (
              <div className="error-text">
                {formik.errors.allSearchFieldsEmpty}
              </div>
            )}
          </Col>
          {hasAccess && (
            <Col md="auto" style={{ marginTop: "26px", marginLeft: "auto" }}>
              <Button
                onClick={() => navigate("/app/create-patient")}
                title="Create Patient"
              >
                <img
                  style={{ width: "26px", height: "26px" }}
                  src={addUserIcon}
                  alt="Create Patient"
                />
                Create Patient
              </Button>
            </Col>
          )}
        </Row>
      </div>

      {/* Results table */}
      <div className="table-wrap">
        <Table responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Date Of Birth</th>
              <th>Email</th>
              <th>Phone Number</th>
              <th
                style={{
                  textAlign: "center",
                }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {btnLoading ? (
              [1, 2, 3, 4, 5].map((val) => (
                <tr key={val}>
                  <td><Skeleton animation="wave" /></td>
                  <td><Skeleton animation="wave" /></td>
                  <td><Skeleton animation="wave" /></td>
                  <td><Skeleton animation="wave" /></td>
                  <td><Skeleton animation="wave" /></td>
                </tr>
              ))
            ) : searchData && searchData.length > 0 ? (
              searchData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((patient) => renderRow(patient))
            ) : (
              <tr>
                <TableCell colSpan={5} style={{ textAlign: "center" }}>
                  No Data Available
                </TableCell>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {searchData && searchData.length > 0 && (
        <div className="pagination-wrap">
          <p
            style={{
              marginBottom:
                Math.ceil(searchData.length / rowsPerPage) > 1 ? "10px" : "0",
            }}
          >
            {`Showing ${
              searchData.length > 0
                ? Math.min(page * rowsPerPage + 1, searchData.length)
                : 0
            }-${
              searchData.length > 0
                ? Math.min(page * rowsPerPage + rowsPerPage, searchData.length)
                : 0
            } of ${searchData.length}`}
          </p>
          <Pagination
            page={page + 1}
            count={Math.ceil(searchData.length / rowsPerPage)}
            color="primary"
            boundaryCount={1}
            onChange={(_, newPage) => setPage(newPage - 1)}
          />
        </div>
      )}
    </section>
  );
}
export default Patients;
